import { EmailStatus, RewardStatus } from './CampaignRecipients.model';

export type StatusCategory = 'completed' | 'pending' | 'failure' | 'info';

export type StatusType = 'email' | 'reward';

export interface StatusFilterSelection {
  statusType: StatusType;
  category: StatusCategory;
}

export const EMAIL_STATUS_CATEGORY: Record<EmailStatus, StatusCategory> = {
  [EmailStatus.Pending]: 'pending',
  [EmailStatus.Sent]: 'completed',
  [EmailStatus.Delivered]: 'completed',
  [EmailStatus.Opened]: 'completed',
  [EmailStatus.Failed]: 'failure',
  [EmailStatus.FailedProcessing]: 'failure',
  [EmailStatus.Dropped]: 'info',
  [EmailStatus.Spam]: 'info',
  [EmailStatus.Unsubscribed]: 'info',
  [EmailStatus.NotAttempted]: 'info',
};

export const REWARD_STATUS_CATEGORY: Record<RewardStatus, StatusCategory> = {
  [RewardStatus.Pending]: 'pending',
  [RewardStatus.Loaded]: 'completed',
  [RewardStatus.RedeemedIssuedLocation]: 'completed',
  [RewardStatus.RedeemedOtherLocation]: 'completed',
  [RewardStatus.Failed]: 'failure',
  [RewardStatus.DuplicateRewardExclusion]: 'info',
  [RewardStatus.LocationRewardExclusion]: 'info',
};

export const STATUS_CATEGORY_LABELS: Record<StatusCategory, string> = {
  completed: 'Completed',
  pending: 'In Progress',
  failure: 'Failed',
  info: 'Excluded',
};

export const STATUS_CATEGORY_ORDER: StatusCategory[] = ['completed', 'pending', 'failure', 'info'];

// Short, table-friendly labels for each specific status - the full enum values are
// complete sentences meant for a tooltip, not compact inline display.
export const EMAIL_STATUS_SHORT_LABELS: Record<EmailStatus, string> = {
  [EmailStatus.Pending]: 'Pending',
  [EmailStatus.Sent]: 'Sent',
  [EmailStatus.Delivered]: 'Delivered',
  [EmailStatus.Opened]: 'Opened',
  [EmailStatus.Failed]: 'Delivery Failed',
  [EmailStatus.FailedProcessing]: 'Processing Failed',
  [EmailStatus.Dropped]: 'Dropped',
  [EmailStatus.Spam]: 'Marked as Spam',
  [EmailStatus.Unsubscribed]: 'Unsubscribed',
  [EmailStatus.NotAttempted]: 'Not Attempted',
};

export const REWARD_STATUS_SHORT_LABELS: Record<RewardStatus, string> = {
  [RewardStatus.Pending]: 'Pending',
  [RewardStatus.Loaded]: 'Loaded',
  [RewardStatus.Failed]: 'Failed to Send',
  [RewardStatus.DuplicateRewardExclusion]: 'Duplicate Reward',
  [RewardStatus.LocationRewardExclusion]: 'Location Exclusion',
  [RewardStatus.RedeemedIssuedLocation]: 'Redeemed (Your Location)',
  [RewardStatus.RedeemedOtherLocation]: 'Redeemed (Other Location)',
};

export function getEmailStatusCategory(status: EmailStatus): StatusCategory {
  return EMAIL_STATUS_CATEGORY[status] ?? 'info';
}

export function getRewardStatusCategory(status: RewardStatus): StatusCategory {
  return REWARD_STATUS_CATEGORY[status] ?? 'info';
}

export function getEmailStatusShortLabel(status: EmailStatus): string {
  return EMAIL_STATUS_SHORT_LABELS[status] ?? status;
}

export function getRewardStatusShortLabel(status: RewardStatus): string {
  return REWARD_STATUS_SHORT_LABELS[status] ?? status;
}

// Groups the specific statuses that roll up into each StatusCategory, preserving the
// declaration order of categoryMap (e.g. Sent, Delivered, Opened for email's 'completed').
export function groupStatusesByCategory<T extends string>(categoryMap: Record<T, StatusCategory>): Map<StatusCategory, T[]> {
  const grouped = new Map<StatusCategory, T[]>();
  (Object.keys(categoryMap) as T[]).forEach(status => {
    const category = categoryMap[status];
    const statuses = grouped.get(category) ?? [];
    statuses.push(status);
    grouped.set(category, statuses);
  });
  return grouped;
}
